import { useRef } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { FotoDeFila } from "@/lib/informes";

import { CampoDeLeyenda } from "./CampoDeLeyenda";
import { motivoDeRechazoDeLaImagen } from "./TarjetaDeImagen";

interface FotosDeLaFilaProps {
  /** "Fotos de la máquina/equipo (una o más)". */
  titulo: string;
  /**
   * El nombre accesible del selector de archivos: "Adjuntar fotos de la
   * máquina o equipo".
   *
   * VA APARTE DEL TÍTULO y no se deriva de él, por dos motivos. El primero es
   * que dice una ACCIÓN y el título dice un contenido, que es lo que un lector
   * de pantalla necesita sobre un `<input type=file>`. El segundo es que el
   * `<Label>` de arriba ya usa el título: con el mismo texto en los dos,
   * `getByLabelText` encontraría dos elementos y ninguna prueba podría
   * dirigirse al selector.
   */
  etiquetaDelSelector: string;
  /** Si hay una operación de fila en curso. */
  ocupada: boolean;
  /**
   * Si el formulario está CORRIGIENDO una fila que ya existe.
   *
   * Decide qué lista se dibuja, y no se deduce de que `guardadas` tenga algo:
   * una fila en edición SIN fotos tiene que mostrar la galería vacía, no la
   * lista de pendientes de un alta que no está ocurriendo.
   */
  editando: boolean;
  /** Las elegidas antes de que la fila exista. Solo en el alta. */
  pendientes: File[];
  /** Las que ya están en el servidor. Solo en la edición. */
  guardadas: FotoDeFila[];
  /** Arma la url de lectura de cada foto. La pantalla sabe el informe; esto no. */
  urlDe: (foto: FotoDeFila) => string;
  /** El texto alternativo de las miniaturas: "Foto de Casa patronal". */
  alt: string;
  /** El rechazo del NAVEGADOR (tipo y peso), o `null`. */
  error: string | null;
  /** Los archivos que pasaron el chequeo del navegador. */
  onElegidas: (archivos: File[]) => void;
  /** El motivo del primer archivo rechazado, o `null` si entraron todos. */
  onRechazo: (motivo: string | null) => void;
  onQuitarPendiente: (indice: number) => void;
  onQuitarGuardada: (fotoId: number) => void;
  /**
   * Con él, cada foto guardada lleva su leyenda libre, que se guarda sola al
   * salir del campo (no cuenta como cambio sin guardar del formulario).
   */
  onLeyenda?: (fotoId: number, leyenda: string | null) => void;
}

/**
 * El bloque de fotos de UNA fila, dentro del formulario que la carga.
 *
 * NACIÓ POR EXTRACCIÓN de `PestanaDeMaquinarias` cuando entraron instalaciones
 * y construcciones: las tres pestañas con cuadro tienen el mismo bloque, con
 * el mismo baile de retener archivos antes de que la fila exista.
 *
 * NO ES `GaleriaDeFotografias`, aunque se parezcan. Aquella es una TARJETA
 * propia, con su título y su badge, para las fotos que cuelgan del INFORME
 * (los encuadres de suelos); esta vive DENTRO de la tarjeta del formulario y
 * sabe algo que aquella no necesita: que en el alta las fotos todavía no se
 * pueden subir. Meter las dos en un componente obligaría a un `if` sobre eso
 * en cada línea del render.
 *
 * EN EL ALTA SE LISTAN POR NOMBRE y en la EDICIÓN van MINIATURAS. No es una
 * inconsistencia: en el alta no hay nada que servir —la fila no existe, así
 * que no hay url— y el nombre del archivo es lo único que distingue una de
 * otra en ese momento. Una vez guardadas, la miniatura responde mejor la única
 * pregunta que uno se hace mirándolas, que es si la foto es de esta fila.
 *
 * EL RECHAZO DEL NAVEGADOR LO APLICA ESTE COMPONENTE y lo INFORMA hacia
 * arriba, porque el estado del error vive en la pantalla junto con el resto
 * del formulario: así el mensaje se limpia cuando se limpia el formulario, sin
 * que este componente tenga que enterarse.
 */
export function FotosDeLaFila({
  titulo,
  etiquetaDelSelector,
  ocupada,
  editando,
  pendientes,
  guardadas,
  urlDe,
  alt,
  error,
  onElegidas,
  onRechazo,
  onQuitarPendiente,
  onQuitarGuardada,
  onLeyenda,
}: FotosDeLaFilaProps) {
  const selector = useRef<HTMLInputElement>(null);

  const cuantas = editando ? guardadas.length : pendientes.length;

  function elegir(archivos: FileList | null) {
    if (archivos === null || archivos.length === 0) return;

    const elegidos = [...archivos];

    // El PRIMER rechazo es el que se muestra: con cinco archivos y tres malos,
    // una lista de tres mensajes ocupa más que el formulario y dice lo mismo.
    const motivo = elegidos.map(motivoDeRechazoDeLaImagen).find((m) => m !== null) ?? null;

    onRechazo(motivo);

    if (motivo === null) onElegidas(elegidos);
  }

  return (
    <div className="border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label className="mb-0">{titulo}</Label>
        <div className="flex items-center gap-3">
          <span className="text-ink-hint text-caption font-mono">
            {cuantas} foto{cuantas === 1 ? "" : "s"}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={ocupada}
            onClick={() => selector.current?.click()}
          >
            + Adjuntar fotos
          </Button>
        </div>
      </div>

      <input
        ref={selector}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        /* `sr-only` y no `hidden` para que el control siga existiendo para la
           tecnología asistiva — mismo criterio que `TarjetaDeImagen`. */
        className="sr-only"
        aria-label={etiquetaDelSelector}
        onChange={(e) => {
          elegir(e.target.files);
          // Se limpia SIEMPRE, incluso cuando el archivo se acepta: sin esto,
          // volver a elegir el mismo archivo no dispara `change` —el valor no
          // cambió— y reintentar después de un error del servidor sería
          // imposible sin elegir otro archivo primero.
          e.target.value = "";
        }}
      />

      {error === null ? null : (
        <p className="text-danger-soft-foreground text-caption mt-2">{error}</p>
      )}

      {editando ? (
        <ul className="mt-3 flex flex-wrap gap-3">
          {guardadas.map((foto) => (
            <li key={foto.id} className={`space-y-1 ${onLeyenda ? "w-36" : ""}`}>
              <img
                src={urlDe(foto)}
                alt={alt}
                className="size-20 rounded-md border object-cover"
              />
              {onLeyenda && (
                <CampoDeLeyenda
                  valor={foto.leyenda ?? null}
                  etiqueta={`Leyenda de la foto ${foto.id}`}
                  disabled={ocupada}
                  onGuardar={(leyenda) => onLeyenda(foto.id, leyenda)}
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={ocupada}
                onClick={() => onQuitarGuardada(foto.id)}
              >
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="mt-3 space-y-1">
          {pendientes.map((archivo, indice) => (
            <li
              key={`${archivo.name}-${indice}`}
              className="text-ink-muted flex items-center justify-between gap-3 text-sm"
            >
              <span className="truncate">{archivo.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onQuitarPendiente(indice)}
              >
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
